import { DataTableColumnHeader } from "@/app/components/table/DataTableColumnHeader";
import ConsignorBadge from "@/app/components/ConsignorBadge";
import { Consignor, ConsignorGroup, ConsignorGroupTier } from "@prisma/client";
import { Flex } from "@radix-ui/themes";
import { ColumnDef, Row } from "@tanstack/react-table";
import Link from "next/link";
import { EMPTY_SENTINEL_VALUE } from "@/lib/constants";
import { DataTableRowActions } from "./DataTableRowActions";
import ConsignorGroupSelector from "./groups/ConsignorGroupSelector";

export type AugmentedConsignor = Consignor & {
  consignorGroup: ConsignorGroup & {
    tiers: ConsignorGroupTier[];
  };
}

// Allow search through any column in consignor table
const consignorSearchFn = <TData extends AugmentedConsignor>(
  row: Row<TData>,
  id: string,
  filterValue: any
) => {
  if (filterValue == EMPTY_SENTINEL_VALUE) {
    const value = row.getValue(id);
    return Boolean(!value);
  }

  if (!filterValue) return true;

  const searchRows = [
    row.original.cocode,
    row.original.name + " " + row.original.lastName,
    row.original.email
  ];

  return (
    searchRows
      .some(field => field?.toLowerCase().includes(filterValue.toLowerCase()))
  );
};

export const columns: ColumnDef<AugmentedConsignor>[] = [
  {
    accessorKey: "cocode",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="CoCode" />
    ),
    cell: ({ row }) => {
      const { original } = row;
      return (
        <Link href={`/consignors/${original.cocode}`}>
          <Flex>
            <ConsignorBadge cocode={original.cocode} />
          </Flex>
        </Link>
      );
    },
    filterFn: consignorSearchFn
  },
  {
    accessorKey: "name",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Name" />
    ),
    cell: ({ row }) => {
      const { original } = row;
      return <Link href={`/consignors/${original.cocode}`}>
        <span>{(!original.name && !original.lastName) ? '-' : `${original.name || ''} ${original.lastName || ''}`.trim()}</span>
      </Link>;
    },
    filterFn: consignorSearchFn
  },
  {
    accessorKey: "email",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Email" />
    ),
    cell: ({ row }) => {
      const { original } = row;
      return (
        <Link href={`/consignors/${original.cocode}`}>
          <Flex>{original.email ?? '-'}</Flex>
        </Link>
      )
    },
  },
  {
    accessorKey: "address",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Address" />
    ),
    cell: ({ row }) => {
      const { original } = row;
      return (
        <Link href={`/consignors/${original.cocode}`}>
          <Flex>{original.address ?? '-'}</Flex>
        </Link>
      )
    },
  },
  {
    accessorKey: "consignorGroup.name",
    maxSize: 50,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Group" />
    ),
    cell: ({ row }) => {
      const { original: { cocode, consignorGroup } } = row;
      return <ConsignorGroupSelector cocode={cocode} currentGroup={consignorGroup} />;
    },
    sortingFn: (
      rowA,
      rowB
    ) => {
      const groupNameA = rowA.original.name ?? '';
      const groupNameB = rowB.original.name ?? '';
      return groupNameA.localeCompare(groupNameB);
    },
    filterFn: (row, id, value) => {
      return value.includes(row.getValue(id))
    },
  },
  {
    accessorKey: "notes",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Notes" />
    ),
    cell: ({ row }) => {
      const { original } = row;
      return (
        <Link href={`/consignors/${original.cocode}`}>
          <Flex>{original.notes ?? '-'}</Flex>
        </Link>
      )
    },
  },
  {
    id: "actions",
    size: 20,
    cell: ({ row }) => <DataTableRowActions row={row} />
  }
]
